<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';

import { useTagStore } from "@/stores/tags-store";

import RsManageTagsFile from "./ManageTagsFile.vue";
import RsManageTagsFilter from "./ManageTagsFilter.vue";

import ConfirmPopover from "@/components/ConfirmPopover.vue";

import type { TagType } from "@/models/TagType";

defineProps<{
  primaryMode: string;
}>();

let name = _ref("");
let errorMessage = _ref<string | null>(null);
let managingTagType = _ref<TagType | null>(null);

const inManageMode = _computed(() => {
  return managingTagType.value !== null;
});

async function addTag() {
  errorMessage.value = null;
  if (name.value === "") {
    errorMessage.value = "Please enter a name";
    return;
  }
  if (!/^([a-zA-Z0-9 _-]+)$/.test(name.value)) {
    errorMessage.value = 'Name can only contain "Alphanumeric", " "(space), "-"(hyphen), "_"(underscore) characters';
    return;
  }
  const isDuplicate = useTagStore().isDuplicate({ name: name.value });
  if (!isDuplicate) {
    await useTagStore().saveTag({ name: name.value, tagType: managingTagType.value!.name });
    name.value = "";
  } else {
    errorMessage.value = `${name.value} already exists!`;
  }
}

async function deleteTag(n: string) {
  await useTagStore().deleteTag({ name: n });
  name.value = "";
}

const getTagsByType = _computed(() => {
  return useTagStore().tags.filter((a) => a.tagType === managingTagType.value?.name);
});

const isFixedType = _computed(() => {
  if (managingTagType.value === null) {
    return false;
  }

  return managingTagType.value.tagRestriction.startsWith("fixed");
});

const tagsUsedIn = _computed(() => {
  return (tagName: string) => useTagStore().fileTags.filter((f) => f.name === tagName).length;
});

function onEnterManagementMode(tagType: TagType): void {
  errorMessage.value = null;
  name.value = "";
  managingTagType.value = tagType;
}

function cancelEdit(): void {
  managingTagType.value = null;
}
</script>
<template>
  <div>
    <div v-if="!inManageMode && primaryMode === 'tagFiles'">
      <RsManageTagsFile @enter-management-mode="onEnterManagementMode"></RsManageTagsFile>
    </div>
    <div v-if="!inManageMode && primaryMode === 'filterTags'">
      <RsManageTagsFilter @enter-management-mode="onEnterManagementMode"></RsManageTagsFilter>
    </div>
    <div v-if="inManageMode">
      <table class="tw-table-dark tw-rounded">
        <thead>
          <tr>
            <th colspan="2">
              Managing:
              <strong>{{ managingTagType?.name }}</strong>
              <button class="tw-btn-sm-white tw-b-r tw-float-right" @click.prevent="cancelEdit">&times;</button>
            </th>
          </tr>
          <tr v-if="!isFixedType">
            <th colspan="2">
              <form class="tw-px-2" @submit.prevent.stop="addTag">
                <div class="tw-grid tw-grid-cols-12">
                  <input
                    v-model.trim="name"
                    type="text"
                    class="tw-col-span-10 tw-rounded-sm tw-border tw-p-1 tw-text-gray-800"
                    placeholder="Name"
                  />
                  <div class="tw-col-span-2 tw-ml-2">
                    <button type="submit" class="tw-rounded-sm tw-border tw-bg-gray-200 tw-p-1 tw-text-gray-800">
                      Add
                    </button>
                  </div>
                  <span class="tw-col-span-12 tw-italic">Enter a new name to add to {{ managingTagType?.name }}</span>
                  <div class="tw-col-span-12 tw-text-red-700">
                    {{ errorMessage }}
                  </div>
                </div>
              </form>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="tag in getTagsByType" :key="tag.name">
            <td>{{ tag.name }}</td>
            <td v-if="!isFixedType" class="tw-text-right">
              <button
                v-if="!tagsUsedIn(tag.name)"
                :id="`tag-${tag.name}`"
                type="button"
                class="tw-btn-sm-white tw-b-r"
                @click.prevent="deleteTag(tag.name)"
              >
                Delete
              </button>
              <ConfirmPopover v-else confirm-text="Delete" @confirmed="() => deleteTag(tag.name)">
                <template #trigger>
                  <div class="tw-btn-sm-white tw-b-r">Delete</div>
                </template>
                <template #question>
                  This tag is used in {{ tagsUsedIn(tag.name) }} files. This action will remove this tag from those
                  files. Are you sure, you want to delete this tag?
                </template>
              </ConfirmPopover>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
<style lang="scss">
.invalid-feedback {
  display: none;
}

.was-validated .invalid-feedback {
  display: block;
}
</style>
